import React from 'react';
import { useAuth } from '@/context/AuthContext';
import Banner from '@/components/widgets/Banner';
import { daysRemaining, getTrialBannerContent } from '@/utils/trial';

/**
 * TrialBanner renders a persistent countdown indicator for users in an active trial.
 * 
 * - Only displays when subscription_status === "trialing" and trial_end is in the future
 * - Non-dismissible throughout the trial
 * - Uses day-by-day copy based on trial days remaining
 */
export const TrialBanner: React.FC = () => {
  const { user } = useAuth();

  // Check if the authenticated user's own account is in an active trial.
  if (user?.subscription_status !== 'trialing' || !user?.trial_end) {
    return null;
  }

  // Calculate days remaining
  const daysLeft = daysRemaining(user.trial_end);

  // If trial has expired (past), don't render
  if (daysLeft <= 0) {
    return null;
  }

  // Get the banner content for this day
  const content = getTrialBannerContent(daysLeft, user.trial_end);

  // Render the banner text, replacing **bold** markers with actual bold
  const renderText = (text: string) => {
    const parts = text.split(/\*\*([^*]+)\*\*/);
    return (
      <>
        {parts.map((part, idx) =>
          idx % 2 === 0 ? (
            <span key={idx}>{part}</span>
          ) : (
            <strong key={idx}>{part}</strong>
          )
        )}
      </>
    );
  };

  return (
    <div className="w-full shrink-0">
      <Banner
        variant="trial"
        dismissible={false}
        sticky={false}
        isFixed={false}
      >
        <div className="flex items-center gap-2">
          <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-vb-warn" />
          <div className="flex-1">{renderText(content.text)}</div>
        </div>
      </Banner>
    </div>
  );
};

export default TrialBanner;
